import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "react-oidc-context";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowDown, ChevronLeft, History, Loader2, Search, Video } from "lucide-react";
import { connectParityApi, normalizeUserId, type MeetingChatMessage } from "@/api/connectApi";
import { useAccessToken } from "@/auth/useAccessToken";
import { useChatReactions, useMeetingChatMembers, useMeetingChatMessages } from "@/api/hooks";
import { flashMessage } from "@/lib/jump";
import { cn } from "@/lib/utils";
import { ConversationFindBar } from "../search/ConversationFindBar";
import { ChatMessageContent } from "../ChatMessageContent";
import { ChatComposer, type ChatShareTarget, type MentionCandidate } from "../chat/ChatComposer";
import { ChatAvatar, dayLabel, sameDay, timeLabel } from "../chat/chatUi";
import { DropOverlay } from "../files/ComposerAttachments";
import { ConversationFiles } from "../files/ConversationFiles";
import { useDriveAttachments, useFileDropZone } from "../files/useDriveAttachments";
import { MeetingRecap } from "./MeetingRecap";

type Row =
    | { type: "day"; key: string; label: string }
    | { type: "msg"; key: string; m: MeetingChatMessage; mine: boolean; first: boolean };

/** A run of messages from one person within this many ms reads as one block. */
const GROUP_GAP_MS = 5 * 60_000;

type Tab = "chat" | "files" | "recap";

/**
 * A meeting's chat, Teams-style — the same conversation before, during and
 * after the meeting. Stored on the server (it used to live only in the room
 * and vanished with it), with the group-chat composer: Markdown, emoji,
 * @mentions of the meeting's people, and Drive files shared with them.
 * Your own messages can be edited or deleted; anyone can react.
 *
 *   variant "page"  — the Chat list's view: header, Join, and Chat | Files | Recap tabs.
 *   variant "panel" — just the conversation and composer, for the meeting room's
 *                     side panel and the calendar's meeting details.
 *
 * Signed-in only; guests in the room use the room's plain-text chat.
 */
export function MeetingChatThread({ meetingId, title, variant, onBack, className }: {
    meetingId: string;
    title: string;
    variant: "page" | "panel";
    onBack?: () => void;
    className?: string;
}) {
    const auth = useAuth();
    const me = normalizeUserId(auth.user?.profile.sub);
    const myEmail = (auth.user?.profile.email ?? "").toLowerCase();
    const token = useAccessToken();
    const qc = useQueryClient();
    const navigate = useNavigate();
    const [tab, setTab] = useState<Tab>("chat");
    // Jump to a search hit that isn't loaded: show the messages around it.
    const [anchor, setAnchor] = useState<string | null>(null);
    const [pendingJump, setPendingJump] = useState<string | null>(null);
    const [findOpen, setFindOpen] = useState(false);

    const query = useMeetingChatMessages(meetingId, anchor);
    const members = useMeetingChatMembers(meetingId);
    const reactions = useChatReactions("meetings", meetingId);
    const messages = useMemo(() => query.data ?? [], [query.data]);

    const jumpTo = (messageId: string) => {
        setTab("chat");
        setPendingJump(messageId);
        if (!messages.some((m) => m.id === messageId)) setAnchor(messageId);
    };
    useEffect(() => {
        if (!pendingJump) return;
        if (messages.some((m) => m.id === pendingJump)) {
            const id = pendingJump;
            setPendingJump(null);
            window.requestAnimationFrame(() => flashMessage(`msg-${id}`));
        } else if (query.isSuccess && !query.isFetching && anchor === pendingJump) {
            setPendingJump(null);
        }
    }, [pendingJump, messages, query.isSuccess, query.isFetching, anchor]);

    // The page header needs the meeting (Join button); the panel lives inside it.
    const meeting = useQuery({
        queryKey: ["meeting", meetingId, token],
        queryFn: () => connectParityApi.getMeeting(meetingId, token),
        enabled: variant === "page" && Boolean(token),
        staleTime: 30_000
    });

    const rows = useMemo<Row[]>(() => {
        const out: Row[] = [];
        let prev: MeetingChatMessage | null = null;
        for (const m of messages) {
            const at = new Date(m.sentAt);
            const prevAt = prev ? new Date(prev.sentAt) : null;
            const newDay = !prevAt || !sameDay(prevAt, at);
            if (newDay) out.push({ type: "day", key: `day-${m.id}`, label: dayLabel(at) });
            const first = newDay
                || prev!.authorUserId !== m.authorUserId
                || at.getTime() - prevAt!.getTime() > GROUP_GAP_MS;
            out.push({ type: "msg", key: m.id, m, mine: normalizeUserId(m.authorUserId) === me, first });
            prev = m;
        }
        return out;
    }, [messages, me]);

    // Mark read on open and as messages arrive, while the tab is visible.
    const markRead = useCallback(() => {
        if (!token || document.visibilityState !== "visible") return;
        void connectParityApi.markMeetingChatRead(meetingId, token)
            .then(() => qc.invalidateQueries({ queryKey: ["chats"] }))
            .catch(() => undefined);
    }, [token, meetingId, qc]);
    useEffect(() => { if (tab === "chat") markRead(); }, [markRead, messages.length, tab]);
    useEffect(() => {
        const onVisible = () => markRead();
        document.addEventListener("visibilitychange", onVisible);
        return () => document.removeEventListener("visibilitychange", onVisible);
    }, [markRead]);

    // Land at the bottom; afterwards follow new messages only when you're at
    // the bottom already or they're yours — otherwise count them on a pill.
    const scrollerRef = useRef<HTMLDivElement>(null);
    const atBottomRef = useRef(true);
    const [newBelow, setNewBelow] = useState(0);
    const [showJump, setShowJump] = useState(false);
    const seenCount = useRef<number | null>(null);
    useEffect(() => { seenCount.current = null; }, [anchor]);
    useLayoutEffect(() => {
        const el = scrollerRef.current;
        if (!el || messages.length === 0) return;
        if (seenCount.current === null) {
            seenCount.current = messages.length;
            el.scrollTop = el.scrollHeight;
            return;
        }
        const added = messages.length - seenCount.current;
        seenCount.current = messages.length;
        if (added <= 0) return;
        const lastMine = normalizeUserId(messages[messages.length - 1]!.authorUserId) === me;
        if (atBottomRef.current || lastMine) {
            el.scrollTop = el.scrollHeight;
            setNewBelow(0);
        } else {
            setNewBelow((n) => n + added);
        }
    }, [messages, me, tab]);

    const onScroll = () => {
        const el = scrollerRef.current;
        if (!el) return;
        const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
        atBottomRef.current = atBottom;
        setShowJump(!atBottom);
        if (atBottom) setNewBelow(0);
    };

    const send = async (html: string) => {
        setAnchor(null);
        await connectParityApi.sendMeetingMessage(meetingId, html, token);
        await qc.invalidateQueries({ queryKey: ["meeting-chat", meetingId] });
        void qc.invalidateQueries({ queryKey: ["chats"] });
    };

    // Files go to the sender's Drive and everyone in the meeting — organizer,
    // invitees and people who joined — gets edit access, as in a group chat.
    const attachments = useDriveAttachments("edit");
    const threadDrop = useFileDropZone(attachments.addFiles);
    const resolveShare = async (): Promise<ChatShareTarget> => {
        const list = members.data ?? (await members.refetch()).data ?? [];
        return {
            emails: list.map((m) => m.email).filter((e) => e && e !== myEmail),
            ownerEmail: myEmail,
            source: "connect-chat",
            sourceRef: meetingId
        };
    };

    const mentionCandidates = useMemo<MentionCandidate[]>(() =>
        (members.data ?? [])
            .filter((m) => m.userId && normalizeUserId(m.userId) !== me)
            .map((m) => ({ userId: m.userId, name: m.displayName || m.email, email: m.email })),
    [members.data, me]);

    const [joining, setJoining] = useState(false);
    const m = meeting.data;
    const canJoin = m ? m.status === "Live" || m.status === "Scheduled" : false;
    const join = async () => {
        if (!m || joining) return;
        setJoining(true);
        try {
            if (m.status === "Scheduled" && normalizeUserId(m.hostUserId) === me) {
                await connectParityApi.startMeeting(m.id, token);
            }
            navigate(`/meeting/${m.id}`);
        } finally {
            setJoining(false);
        }
    };

    const conversation = (
        <>
            {anchor ? (
                <div className="flex shrink-0 items-center gap-2 border-b bg-muted/40 px-4 py-1.5 text-xs" role="status">
                    <History className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
                    <span className="flex-1 text-muted-foreground">You're viewing older messages.</span>
                    <button type="button" onClick={() => setAnchor(null)} className="font-medium text-primary hover:underline">
                        Jump to latest
                    </button>
                </div>
            ) : null}
            <div className="relative min-h-0 flex-1" {...threadDrop.dropProps}>
                {threadDrop.isDragging ? <DropOverlay label="Drop files to share with the meeting" /> : null}
                <div
                    ref={scrollerRef}
                    onScroll={onScroll}
                    role="log"
                    aria-live="polite"
                    aria-label={`Chat in ${title}`}
                    className="h-full overflow-y-auto py-3"
                >
                    {query.isPending ? (
                        <div className="flex h-full items-center justify-center gap-2 text-sm text-muted-foreground">
                            <Loader2 className="h-4 w-4 animate-spin" /> Loading messages…
                        </div>
                    ) : query.isError ? (
                        <div className="flex h-full items-center justify-center px-4 text-center text-sm text-destructive">
                            Couldn't load the meeting chat. It will retry shortly.
                        </div>
                    ) : messages.length === 0 ? (
                        <div className="flex h-full flex-col items-center justify-center gap-2 px-4 text-center text-sm text-muted-foreground">
                            <ChatAvatar name={title} meeting />
                            <p>No messages yet. Everyone in the meeting can see what's posted here — before, during and after.</p>
                        </div>
                    ) : (
                        <ol className="flex flex-col">
                            {rows.map((row) => {
                                if (row.type === "day") {
                                    return (
                                        <li key={row.key} className="my-3 flex items-center gap-2.5 px-4 text-xs text-[var(--connect-text-3)]" role="separator">
                                            <span className="h-px flex-1 bg-[var(--connect-line)]" />
                                            {row.label}
                                            <span className="h-px flex-1 bg-[var(--connect-line)]" />
                                        </li>
                                    );
                                }
                                const { m: msg, mine, first } = row;
                                const name = msg.authorDisplayName || msg.authorEmail || (msg.isGuest ? "Guest" : "Someone");
                                const at = new Date(msg.sentAt);
                                return (
                                    <li key={row.key} className={cn("group flex gap-2.5 px-3", first ? "mt-3.5" : "mt-0.5", mine && "flex-row-reverse")}>
                                        {!mine ? (
                                            first ? <ChatAvatar name={name} size="sm" /> : <span className="w-8 shrink-0" aria-hidden />
                                        ) : null}
                                        <div className={cn("flex min-w-0 max-w-[85%] flex-col", variant === "page" && "sm:max-w-[78%]", mine ? "items-end" : "items-start")}>
                                            {first ? (
                                                <div className="mb-[3px] flex items-baseline gap-1.5 px-0.5 text-xs text-[var(--connect-text-2)]">
                                                    {!mine ? <span className="font-semibold text-foreground">{name}</span> : null}
                                                    <time className="tabular-nums" dateTime={msg.sentAt} title={at.toLocaleString()}>{timeLabel(at)}</time>
                                                </div>
                                            ) : null}
                                            <div
                                                id={`msg-${msg.id}`}
                                                className="max-w-full"
                                                title={first ? undefined : at.toLocaleString()}
                                            >
                                                <ChatMessageContent
                                                    scope="meetings"
                                                    containerId={meetingId}
                                                    message={msg}
                                                    reactions={reactions.data}
                                                    bubbleClassName={cn(
                                                        "rounded-lg px-3 py-2",
                                                        mine ? "bg-[var(--connect-primary-soft)]" : "bg-[var(--connect-subtle)]"
                                                    )}
                                                />
                                            </div>
                                        </div>
                                    </li>
                                );
                            })}
                        </ol>
                    )}
                </div>
                {showJump || newBelow > 0 ? (
                    <button
                        type="button"
                        onClick={() => {
                            scrollerRef.current?.scrollTo({ top: scrollerRef.current.scrollHeight, behavior: "smooth" });
                            setNewBelow(0);
                        }}
                        className="absolute bottom-3 left-1/2 flex -translate-x-1/2 items-center gap-1.5 rounded-full border bg-card px-3 py-1.5 text-xs font-medium shadow-md hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                    >
                        {newBelow > 0 ? `${newBelow} new message${newBelow === 1 ? "" : "s"}` : "Jump to latest"}
                        <ArrowDown className="h-3.5 w-3.5" />
                    </button>
                ) : null}
            </div>
            <ChatComposer
                placeholder="Message the meeting"
                onSend={send}
                attachments={attachments}
                resolveShare={resolveShare}
                mentionCandidates={mentionCandidates}
                poll={{ scope: "meetings", conversationId: meetingId }}
            />
        </>
    );

    if (variant === "panel") {
        return <div className={cn("flex min-h-0 flex-1 flex-col bg-background", className)}>{conversation}</div>;
    }

    const tabs: Array<{ id: Tab; label: string }> = [
        { id: "chat", label: "Chat" },
        { id: "files", label: "Files" },
        { id: "recap", label: "Recap" }
    ];
    const onTabKey = (e: React.KeyboardEvent) => {
        if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
        e.preventDefault();
        const i = tabs.findIndex((t) => t.id === tab);
        const next = tabs[(i + (e.key === "ArrowRight" ? 1 : tabs.length - 1)) % tabs.length]!.id;
        setTab(next);
        document.getElementById(`meeting-chat-tab-${next}`)?.focus();
    };

    return (
        <section className={cn("flex min-h-0 min-w-0 flex-1 flex-col bg-card", className)} aria-label={title}>
            <header className="flex h-14 shrink-0 items-center gap-2.5 border-b border-[var(--connect-line)] px-3 sm:px-3.5">
                {onBack ? (
                    <button
                        type="button"
                        onClick={onBack}
                        aria-label="Back to chats"
                        className="flex h-8 w-8 items-center justify-center rounded hover:bg-[var(--connect-hover)] md:hidden"
                    >
                        <ChevronLeft className="h-5 w-5" />
                    </button>
                ) : null}
                <ChatAvatar name={title} meeting size="sm" />
                <div className="min-w-0 flex-1">
                    <h1 className="truncate text-sm font-semibold">{title}</h1>
                    <p className="truncate text-xs text-[var(--connect-text-2)]">
                        {m ? `${new Date(m.scheduledStart).toLocaleString(undefined, { weekday: "short", day: "numeric", month: "short", hour: "numeric", minute: "2-digit" })} · ${m.status === "Live" ? "In progress" : m.status}` : "Meeting chat"}
                        {members.data ? ` · ${members.data.length} people` : ""}
                    </p>
                </div>
                <div role="tablist" aria-label="Meeting chat views" className="flex items-center gap-2.5 self-stretch sm:gap-3.5" onKeyDown={onTabKey}>
                    {tabs.map((t) => (
                        <button
                            key={t.id}
                            id={`meeting-chat-tab-${t.id}`}
                            type="button"
                            role="tab"
                            aria-selected={tab === t.id}
                            tabIndex={tab === t.id ? 0 : -1}
                            onClick={() => setTab(t.id)}
                            className={cn(
                                "relative flex h-full items-center text-[13px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary",
                                tab === t.id
                                    ? "font-semibold text-foreground after:absolute after:inset-x-0 after:bottom-0 after:h-0.5 after:rounded-full after:bg-primary"
                                    : "text-[var(--connect-text-2)] hover:text-foreground"
                            )}
                        >
                            {t.label}
                        </button>
                    ))}
                </div>
                <button
                    type="button"
                    onClick={() => setFindOpen((v) => !v)}
                    aria-pressed={findOpen}
                    aria-label="Search this meeting chat"
                    title="Search this meeting chat"
                    className={cn(
                        "flex h-8 w-8 shrink-0 items-center justify-center rounded hover:bg-[var(--connect-hover)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
                        findOpen && "bg-[var(--connect-selected)] text-primary"
                    )}
                >
                    <Search className="h-4 w-4" />
                </button>
                {canJoin ? (
                    <button
                        type="button"
                        onClick={() => void join()}
                        disabled={joining}
                        className="flex h-[30px] shrink-0 items-center gap-1.5 rounded bg-primary px-2 text-[13px] font-semibold text-primary-foreground hover:bg-[var(--connect-primary-hover)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:opacity-50 sm:px-3"
                    >
                        {joining ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Video className="h-3.5 w-3.5" />}
                        <span className="hidden sm:inline">{m?.status === "Live" ? "Join now" : "Join"}</span>
                    </button>
                ) : null}
            </header>
            {findOpen ? (
                <ConversationFindBar
                    scope="meetings"
                    id={meetingId}
                    label="this meeting's chat"
                    onJump={(hit) => jumpTo(hit.messageId)}
                    onClose={() => setFindOpen(false)}
                />
            ) : null}
            {tab === "files" ? (
                <ConversationFiles scope="meetings" id={meetingId} label="this meeting's chat" />
            ) : tab === "recap" ? (
                <div className="min-h-0 flex-1 overflow-y-auto p-4" role="tabpanel" aria-label="Recap">
                    <MeetingRecap meetingId={meetingId} />
                </div>
            ) : conversation}
        </section>
    );
}
